import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { cloneDeep, filter, find } from 'lodash';
import { useMutation } from '@apollo/client';
import { CREATE_ORDER_ISSUE } from '@/graphql/mutations';
import avo from 'analytics/avo';
import JBImage from 'components/essentials/JBImage';
import CtaButton from 'components/essentials/Cta/CtaButton';
import LabeledTextarea from 'components/consumer/LabeledTextarea';
import OrderIssueToggleSwitch from './OrderIssueToggleSwitch';
import CustomerContactOption from './media/CustomerContactOption';
import IssueMediaUpload from './media/IssueMediaUpload';
import WarrantyMessage from './media/WarrantyMessage';

/** *
 Keeping orderStatus for the reference

const orderStatus = [
  'Delivered',
  'Refunded',
  'Processing Replacement',
  'Returned',
  'Canceled',
  'Request: Return',
  'Shipped',
  'Waiting on Material Selection',
  'Delivery Issue',
  'Repair Processed',
  'Released For Production',
  'Replacement',
  'Return Received',
  'New',
  'New_Not_Charged',
];
 */

const cleanUrl = url => {
  if (!url) return '';
  const urlObj = new URL(url);
  urlObj.search = '';
  urlObj.hash = '';
  return urlObj.toString();
};
const numberOfYearsPassedFromNow = pastDate => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const year = today.getFullYear();
  const tz = pastDate.split('T')[1] ? `T${pastDate.split('T')[1]}` : '';
  const todaysDate = `${year}-${month}-${day}${tz}`;
  return Math.ceil((new Date(todaysDate).getTime() - new Date(pastDate).getTime()) / (1000 * 60 * 60 * 24 * 365.25));
};
const formatedDate = dateString => {
  if (dateString) {
    const date = new Date(dateString);
    const month = date.toLocaleString('default', {
      month: 'short'
    });
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  }
  return '';
};
const mapImigixUrl = imgSrc => imgSrc ? imgSrc.replace('http://jb-img-dev.joybird.com/public_img/', 'https://joybird2.imgix.net/').replace('jb-img-dev.joybird.com/public_img/', 'https://joybird2.imgix.net/').replace('https://jb-img.co/public_img/', 'https://joybird2.imgix.net/').replace('jb-img.co/public_img/', 'https://joybird2.imgix.net/').replace('small_', '') : '';
const OrderIssueForm = ({
  state: {
    step,
    orderLookInfo: {
      email,
      order_id
    },
    orderInfo
  },
  actions: {
    updateStep,
    updateOrderInfo
  }
}) => {
  const [errorMessage, updateErrorMessage] = useState(null);
  const [isSubmitting, updateIsSubmitting] = useState(false);
  const [removedMediaList, updateRemovedMediaList] = useState([]);
  const [createOrderIssue, {
    data,
    loading,
    error
  }] = useMutation(CREATE_ORDER_ISSUE);
  const yearsSinceOrderDelivered = useMemo(() => step === 'order_issue_form' && ['delivered', 'shipped'].includes(orderInfo?.order?.order_status.toLowerCase()) && orderInfo?.order?.delivery_date ? numberOfYearsPassedFromNow(orderInfo?.order?.delivery_date) : 0, [step, orderInfo]);
  const orderDeliveryDate = useMemo(() => step === 'order_issue_form' && orderInfo?.order?.delivery_date?.split('T')[0] ? `Delivered ${formatedDate(orderInfo?.order?.delivery_date?.split('T')[0])}` : null, [step, orderInfo]);
  const submitOrderIssues = useCallback(() => {
    updateErrorMessage(null);
    updateIsSubmitting(true);
    const orderIssues = filter(orderInfo.orderItems, {
      checked: true
    });
    if (orderIssues.length === 0) {
      updateErrorMessage('Please enter your issue details.');
      updateIsSubmitting(false);
      return;
    }
    const emptyIssueDescription = find(orderIssues, item => !item?.issueDescription || item.issueDescription.trim() === '');
    if (emptyIssueDescription) {
      updateIsSubmitting(false);
      updateErrorMessage('Please enter the issue description/problem');
      return;
    }
    const noIssueAttachments = find(orderIssues, item => !item.photos?.length && !item.videos?.length);
    if (noIssueAttachments) {
      updateIsSubmitting(false);
      updateErrorMessage(`Please upload at least one attachment for ${noIssueAttachments.name}`);
      return;
    }
    const emptyPhoneNumber = find(orderIssues, item => item?.phone?.phoneNumber?.trim() === '');
    if (emptyPhoneNumber) {
      updateIsSubmitting(false);
      updateErrorMessage(`Please enter your phone number`);
      return;
    }
    const attachmentUploadPool = find(orderIssues, item => {
      const imagesUploading = find(item.photos, photo => !photo.s3Path);
      if (imagesUploading) return true;
      if (Array.isArray(item.videos) && item.videos.length > 0) {
        const videosUploading = find(item.videos, video => !video.s3Path);
        if (videosUploading) return true;
      }
      return false;
    });
    if (attachmentUploadPool) {
      updateIsSubmitting(false);
      updateErrorMessage(`Please wait, one/more attachments are uploading`);
      return;
    }
    const input = {
      email,
      order_id: parseInt(order_id, 10),
      removed_files: removedMediaList?.map(media => cleanUrl(media?.s3Path)).filter(Boolean) || [],
      order_items: orderIssues.map(orderItem => ({
        order_item_id: parseInt(orderItem.cart_id, 10),
        tags: ['warranty_claim'],
        issue_desc: orderItem.issueDescription,
        phone: orderItem?.phone,
        photos: orderItem.photos.map(photo => ({
          name: photo?.name,
          size: photo?.size,
          type: photo?.type,
          s3: cleanUrl(photo?.s3Path)
        })).filter(Boolean) || [],
        videos: orderItem.videos.map(video => ({
          name: video?.name,
          size: video?.size,
          type: video?.type,
          s3: cleanUrl(video?.s3Path)
        })).filter(Boolean) || []
      }))
    };
    createOrderIssue({
      variables: {
        input
      }
    });
    updateIsSubmitting(true);
  }, [orderInfo, email, order_id, removedMediaList, createOrderIssue]);
  const isAtLeastOneItemChecked = useMemo(() => Object.values(orderInfo.orderItems).some(orderItem => !!orderItem.checked), [orderInfo]);
  const toggleOrderItem = useCallback(itemId => {
    updateErrorMessage(null);
    updateOrderInfo({
      ...orderInfo,
      orderItems: {
        ...orderInfo.orderItems,
        [itemId]: {
          ...orderInfo.orderItems[itemId],
          checked: !orderInfo.orderItems[itemId].checked
        }
      }
    });
  }, [orderInfo, updateOrderInfo, updateErrorMessage]);
  const deleteIssueKey = useCallback((itemId, issueKey) => {
    updateErrorMessage(null);
    const updatedOrderItem = cloneDeep(orderInfo.orderItems[itemId]);
    delete updatedOrderItem[issueKey];
    updateOrderInfo({
      ...orderInfo,
      orderItems: {
        ...orderInfo.orderItems,
        [itemId]: updatedOrderItem
      }
    });
  }, [orderInfo, updateOrderInfo, updateErrorMessage]);
  const updateIssueDetails = useCallback((itemId, issueDetails) => {
    updateErrorMessage(null);
    updateOrderInfo({
      ...orderInfo,
      orderItems: {
        ...orderInfo.orderItems,
        [itemId]: {
          ...orderInfo.orderItems[itemId],
          ...issueDetails
        }
      }
    });
  }, [orderInfo, updateOrderInfo, updateErrorMessage]);
  const updateIssueAttachments = useCallback(async (itemId, attachments) => {
    updateOrderInfo({
      ...orderInfo,
      orderItems: {
        ...orderInfo.orderItems,
        [itemId]: {
          ...orderInfo.orderItems[itemId],
          ...attachments
        }
      }
    });
  }, [orderInfo, updateOrderInfo]);
  useEffect(() => {
    updateIsSubmitting(loading);
    if (!loading && data?.createOrderIssues?.success) {
      updateIsSubmitting(false);
      updateErrorMessage(null);
      avo.warrantyFormSubmitted({
        email,
        orderId: order_id.toString(),
        error: ''
      });
      updateStep('order_issue_success');
    } else if (!loading && data?.createOrderIssues?.errors) {
      const error1 = data?.createOrderIssues?.errors[0].message;
      avo.warrantyFormSubmitted({
        email,
        orderId: order_id.toString(),
        error: error1
      });
      updateIsSubmitting(false);
      updateErrorMessage(error1 || 'Sorry, We are unable to submit your request. Please try again.');
      updateStep('order_issue_form');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, error]);
  if (step !== 'order_issue_form' || !orderInfo) {
    return <div data-xc="warranty-step2-validation" className="py-2 px-3.5 my-4 border-[1px] border-[#d00] bg-[#FFEBE8] text-sm">
        Invalid request
      </div>;
  }
  return <div className="[&.button]:w-full [&.button]:mt-4">
      <div>
        <div className="text-2xl font-bold items-center text-gray flex">
          <span>Order </span>
          <span className="bg-brand rounded text-white p-4 ml-2">
            J-{orderInfo.order.order_id}
          </span>
        </div>

        <div className="mt-8">
          {Object.entries(orderInfo.orderItems).map(([key, orderItem]) => {
          const materialArray = [];
          const imageUrl = mapImigixUrl(orderItem?.img_src);
          if (orderItem.upholstery) {
            materialArray.push(orderItem.upholstery.trim());
          }
          if (orderItem.wood_stain) {
            materialArray.push(orderItem.wood_stain.trim());
          }
          return <div className="py-7 border-t-[1px] border-t-gray-light6" key={`order-issue-cart-item-${key}`}>
                <div className="flex w-full">
                  {!!imageUrl && <div className="items-center bg-gray-light7 flex justify-center mr-6 p-4 w-[85px]">
                      <JBImage src={imageUrl} sizes="25vw" width="78" />
                    </div>}

                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray mb-0">
                      {orderItem.name}
                    </h3>
                    {!!materialArray.length && <h4 className="text-xs text-gray-light1 mb-0">
                        {materialArray.join(', ')}
                      </h4>}
                    {!!orderDeliveryDate && <h4 className="text-xs text-gray-light1 mb-0">
                        {orderDeliveryDate}
                      </h4>}
                    <OrderIssueToggleSwitch key={`cart_item_id-${key}`} itemId={key} isChecked={!!orderItem.checked} boughtAsStocked={orderItem.bought_as_stocked} stockDiscount={orderItem.stock_discount} isClearance={orderItem.is_clearance} toggleOrderItem={toggleOrderItem} yearsSinceOrderDelivered={yearsSinceOrderDelivered} />
                  </div>
                </div>

                {!!orderItem.checked && <div key={`order-issue-options-${key}`} className="rounded py-1 px-1.5">
                    <div className="mt-1 border-b-[1px] border-b-gray-light8">
                      <strong className="text-sm font-bold text-gray block mt-7 mb-2">
                        What&apos;s the issue?
                      </strong>
                    </div>

                    <div className="mt-1">
                      <LabeledTextarea label="Description of problem(s)" data-xc="issue-description-field" onChange={e => {
                  updateIssueDetails(key, {
                    issueDescription: e.target.value
                  });
                }} value={orderItem.issueDescription ?? ''} resizable="vertical" />
                    </div>

                    <IssueMediaUpload actions={{
                updateIssueAttachments,
                updateRemovedMediaList
              }} state={{
                orderCartId: key,
                removedMediaList
              }} />
                    <CustomerContactOption yearsSinceOrderDelivered={yearsSinceOrderDelivered} onChange={contactDetail => {
                if (contactDetail) updateIssueDetails(key, contactDetail);else deleteIssueKey(key, 'phone');
              }} />
                  </div>}
                <WarrantyMessage yearsSinceOrderDelivered={yearsSinceOrderDelivered} />
              </div>;
        })}

          {errorMessage && <div data-xc="warranty-form-validation" className="py-2 px-3.5 my-4 border-[1px] border-[#d00] bg-[#FFEBE8] text-sm">
              {errorMessage}
            </div>}

          {isAtLeastOneItemChecked && <CtaButton type="submit" color="black" noLinkPadding hasArrow={false} styling="solid-button" data-xc="issue-submit-btn" disabled={isSubmitting} onClick={submitOrderIssues} isLoading={!!isSubmitting} className="w-full">
              Send your request
            </CtaButton>}
        </div>
      </div>
    </div>;
};
OrderIssueForm.propTypes = {
  actions: PropTypes.shape({
    updateStep: PropTypes.func,
    updateOrderInfo: PropTypes.func
  }),
  state: PropTypes.shape({
    step: PropTypes.string,
    orderLookInfo: PropTypes.shape({
      email: PropTypes.string,
      order_id: PropTypes.number
    }),
    orderInfo: PropTypes.objectOf(PropTypes.object)
  })
};
export default OrderIssueForm;